import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PlaceIcon from '@mui/icons-material/Place';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { formatDistanceFrom } from '../../utils';
import type { SegmentCardBaseProps, Restaurant } from '../../types';

type SegmentCardMealProps = SegmentCardBaseProps;

function SegmentCardMeal({
  segment,
  isEditMode,
  isBeingDragged,
  shouldGrayOut,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onDelete,
}: SegmentCardMealProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card
      draggable={isEditMode && !isMobile}
      onDragStart={(e) => isEditMode && !isMobile && onDragStart(e)}
      onDragEnd={(e) => isEditMode && !isMobile && onDragEnd(e)}
      onDragOver={(e) => isEditMode && !isMobile && onDragOver(e)}
      onDragEnter={(e) => isEditMode && !isMobile && onDragEnter(e)}
      onDragLeave={(e) => isEditMode && !isMobile && onDragLeave(e)}
      onDrop={(e) => isEditMode && !isMobile && onDrop(e)}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'stretch',
        boxShadow: 1,
        cursor: isEditMode && !isMobile ? 'move' : 'default',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: isBeingDragged ? 'action.hover' : 'background.paper',
        opacity: shouldGrayOut ? 0.4 : 1,
        filter: shouldGrayOut ? 'grayscale(50%)' : 'none',
        '&:hover': {
          boxShadow: 1,
        }
      }}
    >
      {/* Icon Section */}
      <Box sx={{
        width: { xs: '100%', sm: 200 },
        minWidth: { xs: '100%', sm: 200 },
        height: { xs: 80, sm: 180 },
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent'
          }}
        >
          <RestaurantIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{
        flex: 1,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              mb: 1,
              color: 'text.primary'
            }}
          >
            {segment.name || segment.title || 'Meal'}
          </Typography>

          {/* Restaurant Options */}
          {segment.restaurantOptions && segment.restaurantOptions.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {segment.restaurantOptions.map((restaurant: Restaurant, idx: number) => (
                <Box
                  key={restaurant.id || idx}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}
                >
                  {restaurant.website ? (
                    <Typography
                      component="a"
                      href={restaurant.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        display: 'block',
                        mb: 0.5,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {restaurant.name}
                    </Typography>
                  ) : (
                    <Typography variant="subtitle2" fontWeight="bold" color="primary.main" sx={{ mb: 0.5 }}>
                      {restaurant.name}
                    </Typography>
                  )}
                  {restaurant.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                      {restaurant.description.length > 100
                        ? `${restaurant.description.substring(0, 100)}...`
                        : restaurant.description}
                    </Typography>
                  )}
                  {restaurant.address && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <PlaceIcon sx={{ fontSize: 14 }} />
                      {restaurant.address}
                    </Typography>
                  )}
                  {restaurant.distance_meters != null && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <DirectionsWalkIcon sx={{ fontSize: 14 }} />
                      {formatDistanceFrom(restaurant.distance_meters, restaurant.distanceFrom)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Edit Mode: Drag Indicator (desktop) / Up-Down Arrows (mobile) and Delete Button */}
      {isEditMode && (
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 2, sm: 1 },
          px: 2,
          py: { xs: 1.5, sm: 0 },
          width: { xs: '100%', sm: 'auto' },
          borderTop: { xs: '1px solid', sm: 'none' },
          borderLeft: { xs: 'none', sm: '1px solid' },
          borderColor: 'divider',
          bgcolor: { xs: 'grey.50', sm: 'transparent' }
        }}>
          {/* Desktop: Drag indicator */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            <DragIndicatorIcon
              sx={{
                color: 'action.active',
                fontSize: 28,
                cursor: 'move'
              }}
            />
          </Box>

          {/* Mobile: Up/Down arrows */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onMoveUp();
              }}
              disabled={isFirst}
              sx={{
                color: isFirst ? 'action.disabled' : 'action.active',
                minWidth: 44,
                minHeight: 44,
                backgroundColor: 'grey.100',
                '&:hover': {
                  bgcolor: 'grey.200',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'grey.50',
                }
              }}
            >
              <KeyboardArrowUpIcon />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown();
              }}
              disabled={isLast}
              sx={{
                color: isLast ? 'action.disabled' : 'action.active',
                minWidth: 44,
                minHeight: 44,
                backgroundColor: 'grey.100',
                '&:hover': {
                  bgcolor: 'grey.200',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'grey.50',
                }
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>

          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            sx={{
              color: 'action.active',
              backgroundColor: { xs: 'grey.100', sm: 'transparent' },
              minWidth: 44,
              minHeight: 44,
              '&:hover': {
                bgcolor: { xs: 'grey.200', sm: 'transparent' },
                color: 'grey.700'
              }
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Box>
      )}
    </Card>
  );
}

export default SegmentCardMeal;
